<template>
    <section class="admin-status-panel" aria-label="音乐服务状态">
        <div class="admin-status-heading">
            <strong>音乐服务状态</strong>
            <span class="admin-status-badge" :class="{ 'needs-attention': status.needsAttention }">{{ statusLabel }}</span>
        </div>
        <p class="admin-status-desc">{{ error || status.checkReason || '配置 Cookie 后，点击检测确认是否可用。检测成功会保存 QQ 返回的新登录态。' }}</p>
        <div v-if="error" class="admin-status-desc">音乐登录状态暂无法更新，已保留此前的时间记录。请刷新重试；运行信息的采样时间见下方。</div>
        <dl class="admin-status-grid">
            <div><dt>本地 QQ API 服务</dt><dd>{{ status.serviceAvailable === false ? '暂不可访问 / 请求超时' : error ? '请求异常' : status.serviceAvailable ? '可访问' : '等待查询' }}</dd></div>
            <div><dt>手动更新 Cookie</dt><dd>{{ time(status.lastManualUpdateAt) }}</dd></div>
            <div><dt>最近续期尝试</dt><dd>{{ time(status.lastRefreshAttemptAt) }}</dd></div>
            <div><dt>最近续期成功</dt><dd>{{ time(status.lastRefreshSuccessAt) }}</dd></div>
            <div><dt>最近检测</dt><dd>{{ time(status.lastCheckedAt) }}</dd></div>
            <div><dt>最近验证成功</dt><dd>{{ time(status.lastValidatedAt) }}</dd></div>
        </dl>
        <p class="admin-status-desc">续期：{{ refreshLabel }}{{ status.refreshReason ? ' · ' + status.refreshReason : '' }}</p>
        <div class="admin-status-actions">
            <button type="button" :disabled="busy" @click="$emit('request', 'status')">{{ busy ? '请求中…' : '刷新状态' }}</button>
            <button type="button" :disabled="busy" @click="$emit('request', 'check')">检测并同步登录态</button>
            <button type="button" :disabled="busy || !status.configured" @click="$emit('request', 'refresh')">尝试续期</button>
        </div>
        <div v-if="status.needsAttention" class="admin-status-attention">需要留意：检测显示未登录，或续期 / 取链连续失败。可重新检测，必要时在下方更新 Cookie。</div>
        <details v-if="status.recentErrors && status.recentErrors.length" class="admin-status-errors">
            <summary>最近取链异常（{{ status.recentErrors.length }} 条）</summary>
            <ul><li v-for="(item, index) in status.recentErrors" :key="index">{{ time(item.at) }} · {{ item.songId }}<br>{{ item.reason }}</li></ul>
        </details>
        <p class="admin-status-footnote">未记录的时间显示“暂无记录”；可用性检测超过 24 小时后显示待检测。检测成功不保证每首歌曲都有版权或 VIP 权限。</p>
        <section v-if="status.javaRuntime" class="admin-runtime" aria-label="服务器运行状态">
            <strong>服务器运行状态</strong>
            <p class="admin-status-desc">Java 采样时间：{{ time(javaRuntime.sampledAtMillis) }}</p>
            <dl class="admin-status-grid">
                <div><dt>Java 堆内存（已用 / 上限）</dt><dd>{{ memory(javaRuntime.heapUsedBytes) }} / {{ memory(javaRuntime.heapMaxBytes) }}</dd><small>已提交 {{ memory(javaRuntime.heapCommittedBytes) }}</small></div>
                <div><dt>Java 连续运行</dt><dd>{{ uptime(javaRuntime.uptimeMillis) }}</dd></div>
                <div><dt>在线身份 / 浏览器连接</dt><dd>{{ count(javaRuntime.listenerCount) }} / {{ count(javaRuntime.connectionCount) }}</dd></div>
                <div><dt>活跃音频代理请求</dt><dd>{{ count(javaRuntime.activeAudioStreams) }}</dd><small>包含取链等待；一个浏览器可能有多个请求</small></div>
                <div><dt>QQ Node 进程内存（RSS）</dt><dd>{{ nodeRuntime.available ? memory(nodeRuntime.rssBytes) : '暂不可获取' }}</dd><small v-if="nodeRuntime.available">堆内存 {{ memory(nodeRuntime.heapUsedBytes) }} / {{ memory(nodeRuntime.heapLimitBytes) }}</small></div>
                <div><dt>QQ Node 连续运行</dt><dd>{{ nodeRuntime.available ? uptime(nodeRuntime.uptimeMillis) : '暂不可获取' }}</dd></div>
                <div v-if="nodeRuntime.available && nodeRuntime.cache"><dt>Node 元数据缓存（条目 / 上限）</dt><dd>{{ count(nodeRuntime.cache.entryCount) }} / {{ count(nodeRuntime.cache.maxEntries) }}</dd><small>估算 {{ memory(nodeRuntime.cache.estimatedBytes) }} / {{ memory(nodeRuntime.cache.maxEstimatedBytes) }}</small></div>
            </dl>
            <p class="admin-status-footnote">Java 堆内存不包含 JVM 全部占用；Node RSS 是单个进程占用，均不是整机内存。缓存大小为估算值，不是音频文件；运行信息随状态查询采样，无额外高频监控。</p>
        </section>
    </section>
</template>
<script>
import {runtimeMiB, runtimeCount, runtimeUptime} from '../adminRuntime';
export default {
    name: 'AdminStatusPanel',
    props: { status: { type: Object, default: () => ({}) }, busy: Boolean, error: String },
    computed: {
        statusLabel() { return this.error ? '待重新查询' : ({missing: '未配置', unknown: '待检测', suspect: '疑似失效', available: '已验证可用'})[this.status.status] || '等待查询'; },
        refreshLabel() { return ({success: '成功', failed: '失败', unknown: '暂无记录'})[this.status.refreshResult] || '暂无记录'; },
        javaRuntime() { return this.status.javaRuntime || {}; },
        nodeRuntime() { return this.status.nodeRuntime || {}; }
    },
    methods: {
        time(value) { return value ? new Date(value).toLocaleString('zh-CN', {hour12: false}) : '暂无记录'; },
        memory: runtimeMiB,
        count: runtimeCount,
        uptime: runtimeUptime
    }
};
</script>
<style scoped>
.admin-status-panel { padding: 16px; margin-bottom: 20px; border: 1px solid var(--room-border); border-radius: 14px; background: var(--room-input); color: var(--room-text); }
.admin-status-heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.admin-status-badge { padding: 4px 10px; border-radius: 20px; font-size: 12px; color: var(--room-text); background: var(--room-accent-soft); }
.admin-status-badge.needs-attention, .admin-status-attention { color: var(--room-danger); }
.admin-status-desc { color: var(--room-text-secondary); font-size: 13px; line-height: 1.6; }
.admin-status-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin: 16px 0; }
.admin-status-grid dt { font-size: 12px; color: var(--room-text-muted); }
.admin-status-grid dd { margin: 4px 0 0; font-size: 13px; }
.admin-status-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.admin-status-actions button { padding: 8px 12px; border: 1px solid var(--room-border); border-radius: 8px; background: var(--room-panel); color: var(--room-text); cursor: pointer; }
.admin-status-actions button:disabled { opacity: .5; cursor: wait; }
.admin-status-actions button:focus-visible { outline: 2px solid var(--room-accent-border); }
.admin-status-attention, .admin-status-errors { margin-top: 14px; font-size: 13px; line-height: 1.7; }
.admin-status-errors summary { cursor: pointer; }
.admin-status-errors ul { padding-left: 18px; }
.admin-status-errors li { margin-bottom: 8px; overflow-wrap: anywhere; }
.admin-status-footnote { margin: 16px 0 0; color: var(--room-text-muted); font-size: 12px; line-height: 1.6; }
.admin-runtime { border-top: 1px solid var(--room-border); margin-top: 18px; padding-top: 18px; }
.admin-runtime small { display: block; margin-top: 4px; color: var(--room-text-muted); font-size: 12px; line-height: 1.5; }
.admin-runtime dd { overflow-wrap: anywhere; }
@media (max-width: 480px) { .admin-status-grid { grid-template-columns: 1fr; } }
</style>
